import { Button, Dialog, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * The welcome, shown once to phones.
 *
 * It used to apologise for the phone layout and suggest finding a laptop,
 * which was honest at the time and is now the most expensive sentence we could
 * put in front of an arrival: the phone is the layout this is built for.
 *
 * What replaces it is the one thing a phone reader cannot discover by looking.
 * The filter tab is labelled and a tap explains itself, but nothing on screen
 * says that a card can be swiped, or that the pages can — so the notice says it,
 * once, and gets out of the way.
 *
 * No close ✕ and no click-away, unlike every other dialog here. One button, one
 * meaning: they have read it. It costs one tap and it only ever happens once.
 */

const Panel = styled('div')({
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '28px 26px 22px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

const Body = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 14.5,
  lineHeight: 1.62,
  color: 'rgba(243,238,231,0.9)',
});

interface Props {
  open: boolean;
  onDismiss: () => void;
}

export default function MobileNotice({ open, onDismiss }: Props) {
  return (
    <Dialog
      open={open}
      // No `onClose`, so neither a backdrop tap nor Escape dismisses it — MUI
      // only closes what it is given a handler for. The button is the only way
      // out, so the tap means "read" rather than "dismissed".
      maxWidth="xs"
      fullWidth
      aria-labelledby="mobile-notice-heading"
      slotProps={{
        paper: {
          sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 3, m: 2 },
        },
      }}
    >
      <Panel>
        <Typography
          id="mobile-notice-heading"
          component="h2"
          sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, lineHeight: 1.25 }}
        >
          Welcome to CommonGround.
        </Typography>

        <Body>
          Climate action happening near you, in one place. Tap{' '}
          <strong>Search + Filter</strong> on the left, or swipe right, to narrow things down.
          Swipe left to see <strong>Saved</strong>.
        </Body>

        <Body>
          <strong>Swipe a card right</strong> if you're going, or <strong>left</strong> for maybe;
          both land in Saved. Tap it to read the full listing.
        </Body>

        <Button
          onClick={onDismiss}
          fullWidth
          sx={{
            mt: 0.5,
            background: BRAND.parchment,
            color: BRAND.mocha,
            fontFamily: FONTS.body,
            fontWeight: 700,
            fontSize: 14,
            borderRadius: 999,
            py: 1.25,
            '&:hover': { background: '#fff' },
          }}
        >
          Start exploring
        </Button>
      </Panel>
    </Dialog>
  );
}
